import React from 'react';
import '../../styles/components/AsperaBrandingForm.scss';
import { AsperaWrapper } from '../AsperaWrapper';
import { AsperaForm } from '../AsperaForm';
import { language } from '../../services/LanguageService';
import { SubManager } from '../../classes/SubManager';
import { Subject } from 'rxjs';
import { Button, TextInput, getIconComponent } from '../Carbon';
import { AsperaImagePicker } from '../AsperaImagePicker';
import { brandingInAppImage, brandingInAppText, brandingInAppTextImage } from '../../constants/data';
import { AsperaTooltip } from '../AsperaTooltip';
import { createImageUrl, deepClone } from '../../helpers/starterHelpers';
/**
 * Helper for branding pages get text image for tooltips
 */
export const getTextImageTooltip = (text, imageText, image) => {
    return React.createElement(AsperaTooltip, { className: "image-helper", tooltipLabel: text },
        React.createElement("div", { className: "image-helper--tooltip-text" }, imageText),
        React.createElement("img", { className: "image-helper--tooltip-image", src: image, alt: imageText, title: imageText }));
};
/**
 * React Component
 *
 * The AsperaBrandingForm renders branding form (for Header customization). Load directly on component.
 *
 * @example
 * ```javascript
 * <AsperaBrandingForm onSubmit={() => {}} />
 * ```
 */
export class AsperaBrandingForm extends React.Component {
    constructor() {
        super(...arguments);
        this.state = {
            loading: true,
            error: false,
        };
        this.subManager = new SubManager();
        this.localModel = {};
        this.externalDirtySubject = new Subject();
        this.brandingOnLoad = {};
        this.fakeModel = {
            appLogoType: 'DEFAULT',
        };
        this.previewAppLogo = () => {
            const { previewBrandingSubject, displayName } = this.props;
            if (previewBrandingSubject) {
                previewBrandingSubject.next({
                    customText: this.localModel.appLogoText,
                    imageUrl: createImageUrl(this.localModel.appLogoImage),
                    displayName,
                });
            }
        };
        this.save = () => {
            const { onSubmit } = this.props;
            onSubmit(this.localModel);
        };
    }
    get previewAppUiButton() {
        return React.createElement(Button, { kind: "primary", className: "branding-preview-button", renderIcon: getIconComponent('View'), iconDescription: language.get('customBrandingForm.previewInApp'), onClick: this.previewAppLogo }, language.get('customBrandingForm.previewInApp'));
    }
    get inAppLogoView() {
        const image = React.createElement(AsperaImagePicker, { onChange: data => {
                this.localModel.appLogoImage = data;
                this.externalDirtySubject.next(true);
            }, initialData: createImageUrl(this.localModel.appLogoImage), renderWidth: 215, renderHeight: 28, maxSize: 500000 });
        if (typeof this.localModel.appLogoText !== 'string') {
            this.localModel.appLogoText = '';
        }
        const text = (React.createElement(TextInput, { maxLength: 40, id: "in-app-logo-view-text", required: true, value: this.localModel.appLogoText, labelText: language.get('customBrandingForm.customText'), onChange: (event) => {
                this.localModel.appLogoText = event.target.value;
                this.setState({});
            } }));
        switch (this.fakeModel.appLogoType) {
            case 'TEXT':
                return (React.createElement(React.Fragment, null,
                    getTextImageTooltip(language.get('customBrandingForm.inAppTextMode'), language.get('customBrandingForm.customAppTextHere'), brandingInAppText),
                    text));
            case 'IMAGE':
                return (React.createElement(React.Fragment, null,
                    getTextImageTooltip(language.get('customBrandingForm.inAppImageMode'), language.get('customBrandingForm.customAppImageHere'), brandingInAppImage),
                    image));
            case 'TEXT_IMAGE':
                return (React.createElement(React.Fragment, null,
                    getTextImageTooltip(language.get('customBrandingForm.inAppTextImageMode'), language.get('customBrandingForm.customAppTextImageHere'), brandingInAppTextImage),
                    image,
                    text));
            default:
                return null;
        }
    }
    get formDetails() {
        const { previewBrandingSubject } = this.props;
        return {
            model: this.fakeModel,
            id: 'aspera-form-branding-form-container',
            onSubmit: this.save,
            items: [
                { type: 'custom', hidden: () => !previewBrandingSubject, customView: this.previewAppUiButton },
                { type: 'sub-header', label: language.get('customBrandingForm.inAppLogoCustomization'), helperText: language.get('customBrandingForm.inAppLogoCustomizationHelper') },
                {
                    type: 'radio',
                    pointer: 'appLogoType',
                    radioProps: { orientation: 'horizontal' },
                    items: [
                        { value: 'DEFAULT', text: language.get('customBrandingForm.default') },
                        { value: 'TEXT', text: language.get('customBrandingForm.customText') },
                        { value: 'IMAGE', text: language.get('customBrandingForm.customImage') },
                        { value: 'TEXT_IMAGE', text: language.get('customBrandingForm.customTextImage') },
                    ],
                    onChange: value => {
                        switch (value) {
                            case 'TEXT':
                                this.localModel.appLogoImage = undefined;
                                break;
                            case 'IMAGE':
                                this.localModel.appLogoText = undefined;
                                break;
                            case 'DEFAULT':
                                this.localModel.appLogoText = undefined;
                                this.localModel.appLogoImage = undefined;
                                break;
                        }
                        this.setState({});
                    },
                },
                {
                    type: 'custom',
                    hidden: () => this.fakeModel.appLogoType === 'DEFAULT',
                    customView: this.inAppLogoView,
                },
            ],
        };
    }
    componentWillUnmount() {
        const { previewBrandingSubject, displayName } = this.props;
        if (previewBrandingSubject) {
            previewBrandingSubject.next({
                customText: this.brandingOnLoad.appLogoText,
                imageUrl: createImageUrl(this.brandingOnLoad.appLogoImage),
                displayName,
            });
        }
        this.subManager.unsubscribeAll();
    }
    componentDidMount() {
        const { value } = this.props;
        this.brandingOnLoad = deepClone(value);
        this.localModel = value;
        if (this.localModel.appLogoImage && this.localModel.appLogoText) {
            this.fakeModel.appLogoType = 'TEXT_IMAGE';
        }
        else if (this.localModel.appLogoImage) {
            this.fakeModel.appLogoType = 'IMAGE';
        }
        else if (this.localModel.appLogoText) {
            this.fakeModel.appLogoType = 'TEXT';
        }
        this.setState({ loading: false });
    }
    render() {
        const { className } = this.props;
        const { loading, error } = this.state;
        const id = 'aspera-form-branding-form';
        return (React.createElement(AsperaWrapper, { className: `aspera-branding-form ${className ? className : ''}`, loading: loading, error: error, id: id, disableWrapping: true },
            React.createElement(AsperaForm, { formDetails: this.formDetails, externalDirty: this.externalDirtySubject.asObservable(), fullPageWrapperId: id })));
    }
}
